import { useLogto } from "@logto/react";
import { pushAudioLevel } from "components/audio-waveform";
import {
  useEndAskHelenaVoiceSession,
  useEndAskHelenaVoiceSessionOnUnload,
  useStartAskHelenaVoiceSession,
  useUploadAskHelenaVoiceAudio,
} from "controller";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AcceptedDictation,
  AskHelenaVoiceSession,
  AskHelenaVoiceVocabulary,
  DictationIssue,
  DictationStatus,
} from "shared";
import { sentryLogger } from "utilz";
import { useIsVoiceDictationEnabled } from "utilz/featureFlags";
import {
  debugConsole,
  getMicrophonePermissionState,
  isMicrophoneSupported,
  openMicrophone,
  releaseMicrophone,
  toMicrophoneErrorKind,
} from "utilz/helpers";
import {
  type DictationSegment,
  normalizeDictation,
  segmentsToText,
} from "../utils/dictationNormalize";
// Built as its own hashed file: a new name on every change, so nginx's 30-day
// .js cache can never serve a stale worklet.
import workletUrl from "../worklets/askHelenaPcmWorklet?worker&url";

/**
 * Voice dictation: microphone → AudioWorklet → Soniox WebSocket. Used by the
 * Ask Helena home composer and the ticket chat composer.
 * Design: .claude/projects/shared/ask-helena-voice-dictation/DESIGN.md §4–§6.
 *
 * Audio never touches our backend. helena-ask-service mints a single-use key
 * (`startSession`), and is told when the stream ends (`/end`, exactly once per
 * minted session) and, on accept, receives the recording.
 *
 * Capture (mic, worklet, recorder) and transport (the Soniox socket) are kept
 * in separate functions — the seam a vendor swap would need.
 */

/** Registered by the worklet — must match its PROCESSOR_NAME. */
const WORKLET_NAME = "ask-helena-pcm-worklet";
/** English only in v1 (DESIGN §1). */
const DICTATION_LANGUAGE = "en";
const DOMAIN_CONTEXT =
  "IT service desk ticketing for managed service providers";
/** Soniox control markers — never shown. */
const CONTROL_TOKENS = new Set(["<end>", "<fin>"]);
/** Safari records mp4; everything else opus in webm. */
const RECORDER_TYPES = ["audio/webm;codecs=opus", "audio/mp4"];
/** If the recorder never reports, stop waiting for it. */
const RECORDER_STOP_TIMEOUT_MS = 2000;
/**
 * After ✓, how long to wait for Soniox to finalise the grey words before
 * inserting what we have. It normally answers well within this.
 */
const FINALIZE_TIMEOUT_MS = 2000;
/**
 * No new words for this long — from the start, or since the last word — ends
 * the dictation, so a mic left on by someone who walked away closes itself.
 * Closing the stream is the only thing that stops billing: Soniox charges for
 * the whole open stream, not just speech.
 */
const NO_SPEECH_TIMEOUT_MS = 20000;
/** The last stretch of that silence shows a "Stopping in Ns" warning. */
const NO_SPEECH_WARNING_MS = 5000;
/** One live mic across composers and tabs (see the BroadcastChannel effect). */
const LIVE_MIC_CHANNEL = "deskday-voice-dictation-live-mic";
/** Soniox ends a stream at the key's max_session_duration with this error, not `finished`. */
const SESSION_EXPIRED_ERROR = "temp_api_key_session_expired";

interface WorkletChunk {
  pcm: ArrayBuffer;
  level: number;
}

interface Capture {
  ctx: AudioContext;
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  node: AudioWorkletNode;
  /** Null when the caller doesn't keep the recording (`uploadRecording: false`). */
  recorder: MediaRecorder | null;
  recordedChunks: Blob[];
  /** Memoised so every exit path shares one teardown. */
  stopping: Promise<Blob | null> | null;
}

interface StreamSession {
  id: string;
  maxSeconds: number;
  sampleRate: number;
  ws: WebSocket | null;
  opened: boolean;
  /** Set before WE close the socket, so `onclose` doesn't read it as a failure. */
  closing: boolean;
  samplesSent: number;
  openedAt: number;
  ended: boolean;
  /** ✓ was pressed: no more audio is sent, and `finished` resolves the accept. */
  finishing: boolean;
  onFinished: (() => void) | null;
}

interface SonioxToken {
  text: string;
  is_final: boolean;
}

interface SonioxMessage {
  tokens?: SonioxToken[];
  finished?: boolean;
  error_code?: number | string;
  error_type?: string;
}

interface UseVoiceDictationOptions {
  /**
   * The composer unmounted (navigation, search opened) mid-dictation with
   * words on screen. They are accepted as if ✓ was pressed — finalised in the
   * background, then handed here, after unmount — so the caller must write
   * them somewhere that outlives the component (a store atom, a stored draft).
   */
  onLeave?: (accepted: AcceptedDictation) => void;
  /**
   * Record the audio and upload it on ✓, for playback in Ask Helena history.
   * `false` for surfaces with no playback: nothing is recorded or uploaded.
   */
  uploadRecording?: boolean;
}

export const isDictationSupported = (): boolean =>
  isMicrophoneSupported() &&
  typeof AudioWorkletNode !== "undefined" &&
  typeof WebSocket !== "undefined" &&
  typeof MediaRecorder !== "undefined";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const resumeQuietly = async (ctx: AudioContext) => {
  try {
    await ctx.resume();
  } catch {
    // The graph resumes it when audio starts flowing.
  }
};

const closeQuietly = async (ctx: AudioContext) => {
  try {
    await ctx.close();
  } catch {
    // Already closed.
  }
};

/** `resetsAt` may sit at the body root or inside `data`; the shape is unconfirmed (DESIGN §10 B5). */
const findResetsAt = (body: unknown): string | null => {
  if (!isRecord(body)) return null;
  if (typeof body.resetsAt === "string") return body.resetsAt;
  return isRecord(body.data) && typeof body.data.resetsAt === "string"
    ? body.data.resetsAt
    : null;
};

/** The codes the service sends with its 403 and 429 (handoff). */
const DISABLED_CODE = "dictation_disabled";
const MINUTES_EXHAUSTED_CODE = "dictation_minutes_exhausted";

/**
 * Whether the thrown API error carries `code`. Where the service puts it is
 * unconfirmed (DESIGN §10 B5): a custom body has it at the root or in `data`;
 * a plain NestJS `ForbiddenException("dictation_disabled")` has it as the
 * message, which `apiErrorHandler` also copies onto the error itself.
 */
const hasErrorCode = (
  error: Record<string, unknown>,
  code: string,
): boolean => {
  const body = isRecord(error.data) ? error.data : {};
  const inner = isRecord(body.data) ? body.data : {};
  return [body.code, inner.code, body.message, error.message].includes(code);
};

const toSessionIssue = (error: unknown): DictationIssue => {
  if (!isRecord(error)) return { kind: "unavailable" };
  const status = typeof error.status === "number" ? error.status : 0;
  // Only the service's own codes count: a 403 or 429 for another reason (a
  // permission, a rate limit) must not hide the mic or claim the minutes ran out.
  if (status === 403 && hasErrorCode(error, DISABLED_CODE)) {
    return { kind: "disabled" };
  }
  if (status === 429 && hasErrorCode(error, MINUTES_EXHAUSTED_CODE)) {
    return { kind: "minutes_exhausted", resetsAt: findResetsAt(error.data) };
  }
  return { kind: "unavailable" };
};

const isSonioxToken = (value: unknown): value is SonioxToken =>
  isRecord(value) &&
  typeof value.text === "string" &&
  typeof value.is_final === "boolean";

/** Checked field by field — a provider message is not trusted to match its docs. */
const parseSonioxMessage = (data: unknown): SonioxMessage | null => {
  if (typeof data !== "string") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;

  const errorCode = parsed.error_code;
  return {
    tokens: Array.isArray(parsed.tokens)
      ? parsed.tokens.filter(isSonioxToken)
      : undefined,
    finished: parsed.finished === true,
    error_code:
      typeof errorCode === "number" || typeof errorCode === "string"
        ? errorCode
        : undefined,
    error_type:
      typeof parsed.error_type === "string" ? parsed.error_type : undefined,
  };
};

const startRecorder = (
  stream: MediaStream,
): { recorder: MediaRecorder; recordedChunks: Blob[] } => {
  const mimeType = RECORDER_TYPES.find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
  const recorder = new MediaRecorder(
    stream,
    mimeType ? { mimeType } : undefined,
  );
  const recordedChunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size) recordedChunks.push(event.data);
  };
  recorder.start();
  return { recorder, recordedChunks };
};

const startCapture = async (
  ctx: AudioContext,
  onChunk: (chunk: WorkletChunk) => void,
  record: boolean,
  /** The mic went away mid-stream: unplugged, permission revoked, OS lock. */
  onTrackEnded: () => void,
): Promise<Capture> => {
  const stream = await openMicrophone();

  try {
    // Not fired by our own stop() — only when the device or browser ends it.
    for (const track of stream.getAudioTracks()) track.onended = onTrackEnded;
    await ctx.audioWorklet.addModule(workletUrl);
    const source = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, WORKLET_NAME);
    node.port.onmessage = ({ data }: MessageEvent<WorkletChunk>) =>
      onChunk(data);
    source.connect(node);
    // Keeps the node pulled; it outputs silence.
    node.connect(ctx.destination);

    const { recorder, recordedChunks } = record
      ? startRecorder(stream)
      : { recorder: null, recordedChunks: [] };

    return {
      ctx,
      stream,
      source,
      node,
      recorder,
      recordedChunks,
      stopping: null,
    };
  } catch (error: unknown) {
    releaseMicrophone(stream);
    throw error;
  }
};

const waitForRecorder = (recorder: MediaRecorder): Promise<void> =>
  new Promise((resolve) => {
    if (recorder.state === "inactive") {
      resolve();
      return;
    }
    const timeoutId = setTimeout(resolve, RECORDER_STOP_TIMEOUT_MS);
    recorder.onstop = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    recorder.stop();
  });

/** Releases the mic on EVERY exit — the POC leaked it when the socket closed on its own. */
const stopCapture = (capture: Capture): Promise<Blob | null> => {
  if (!capture.stopping) {
    capture.stopping = (async () => {
      capture.node.port.onmessage = null;
      for (const track of capture.stream.getAudioTracks()) {
        track.onended = null;
      }
      if (capture.recorder) await waitForRecorder(capture.recorder);
      releaseMicrophone(capture.stream);
      capture.node.disconnect();
      capture.source.disconnect();
      await closeQuietly(capture.ctx);

      if (!(capture.recorder && capture.recordedChunks.length)) return null;
      return new Blob(capture.recordedChunks, {
        type: capture.recorder.mimeType || capture.recordedChunks[0].type,
      });
    })();
  }
  return capture.stopping;
};

const closeSocket = (session: StreamSession) => {
  session.closing = true;
  const { ws } = session;
  if (!ws) return;
  ws.onopen = null;
  ws.onmessage = null;
  if (
    ws.readyState === WebSocket.OPEN ||
    ws.readyState === WebSocket.CONNECTING
  ) {
    ws.close();
  }
};

const toStreamSession = (
  minted: AskHelenaVoiceSession,
  sampleRate: number,
): StreamSession => ({
  id: minted.sessionId,
  maxSeconds: minted.maxSeconds,
  sampleRate,
  ws: null,
  opened: false,
  closing: false,
  samplesSent: 0,
  openedAt: 0,
  ended: false,
  finishing: false,
  onFinished: null,
});

/**
 * Opens the mic and mints the session — in parallel when the mic is already
 * allowed, otherwise mic first (DESIGN §5 rule 2). Never throws: whichever half
 * succeeded is returned so the caller can release it.
 */
const openMicAndSession = async (
  capture: () => Promise<Capture>,
  mint: () => Promise<AskHelenaVoiceSession>,
): Promise<{
  captured: Capture | null;
  minted: AskHelenaVoiceSession | null;
  failure: DictationIssue | null;
}> => {
  let captureResult: PromiseSettledResult<Capture>;
  let sessionResult: PromiseSettledResult<AskHelenaVoiceSession> | null = null;

  // Already allowed: mint alongside opening the mic. Otherwise the browser
  // will prompt, and minting waits so the 60-second key isn't burned on a
  // prompt the user may leave open. "unknown" (Firefox) counts as a prompt.
  if ((await getMicrophonePermissionState()) === "granted") {
    [captureResult, sessionResult] = await Promise.allSettled([
      capture(),
      mint(),
    ]);
  } else {
    [captureResult] = await Promise.allSettled([capture()]);
    if (captureResult.status === "fulfilled") {
      [sessionResult] = await Promise.allSettled([mint()]);
    }
  }

  let failure: DictationIssue | null = null;
  if (captureResult.status === "rejected") {
    failure = { kind: toMicrophoneErrorKind(captureResult.reason) };
  } else if (sessionResult?.status === "rejected") {
    failure = toSessionIssue(sessionResult.reason);
  }

  return {
    captured: captureResult.status === "fulfilled" ? captureResult.value : null,
    minted: sessionResult?.status === "fulfilled" ? sessionResult.value : null,
    failure,
  };
};

/** A start that failed or was discarded half way: free the mic, end the session. */
const releaseHalfStart = (
  ctx: AudioContext,
  captured: Capture | null,
  endSession: (() => void) | null,
) => {
  if (captured) void stopCapture(captured);
  else void closeQuietly(ctx);
  endSession?.();
};

/**
 * Resolves when Soniox reports `finished` (or the socket ends) after the
 * end-of-audio frame, or after FINALIZE_TIMEOUT_MS — never leaves ✓ hanging.
 */
const waitForFinish = (session: StreamSession): Promise<void> =>
  new Promise((resolve) => {
    const done = () => {
      clearTimeout(timeoutId);
      session.onFinished = null;
      resolve();
    };
    const timeoutId = setTimeout(done, FINALIZE_TIMEOUT_MS);
    session.onFinished = done;
  });

/**
 * ✓ while listening: release the mic, then send the empty frame that tells
 * Soniox the audio is over — it finalises the pending words, sends
 * `finished` and closes. Resolves once that happens (or on timeout).
 */
const finalizeStream = async (
  session: StreamSession,
  capture: Capture | null,
): Promise<void> => {
  session.finishing = true;
  if (capture) void stopCapture(capture);
  if (session.ws?.readyState !== WebSocket.OPEN) return;
  session.ws.send("");
  await waitForFinish(session);
};

/**
 * The leave path runs after unmount — no UI is left to show a toast, so a
 * failure there (a bug, not an API error: those are reported by the controller
 * hooks) goes to Sentry, shaped as the logger expects.
 */
const reportLeaveFailure = (error: unknown) => {
  debugConsole(error);
  sentryLogger({
    error: {
      message: error instanceof Error ? error.message : "Unknown error",
      status: 0,
      traceId: "unknown",
    },
    data: error,
    workflow: "helena",
    functionName: "useVoiceDictation.onLeave",
  });
};

const elapsedSecondsOf = (session: StreamSession): number =>
  Math.round(session.samplesSent / session.sampleRate);

const isSonioxError = (message: SonioxMessage): boolean =>
  message.error_code !== undefined || Boolean(message.error_type);

/** What a Soniox error ends the stream with — the time limit, when it is one. */
const socketErrorIssue = (
  session: StreamSession,
  message: SonioxMessage,
): DictationIssue =>
  message.error_type === SESSION_EXPIRED_ERROR ||
  elapsedSecondsOf(session) >= session.maxSeconds - 1
    ? { kind: "time_limit", maxSeconds: session.maxSeconds }
    : { kind: "interrupted" };

export const useVoiceDictation = ({
  onLeave,
  uploadRecording: recordsAudio = true,
}: UseVoiceDictationOptions = {}) => {
  const { getAccessToken } = useLogto();
  const { mutateAsync: startSession } = useStartAskHelenaVoiceSession();
  const { mutateAsync: endSession } = useEndAskHelenaVoiceSession();
  const { mutateAsync: uploadRecording } = useUploadAskHelenaVoiceAudio();
  const endSessionOnUnload = useEndAskHelenaVoiceSessionOnUnload();
  const isEnabled = useIsVoiceDictationEnabled();

  const [status, setStatus] = useState<DictationStatus>("idle");
  const [finalText, setFinalText] = useState("");
  const [tail, setTail] = useState("");
  const [vocabulary, setVocabulary] = useState<AskHelenaVoiceVocabulary | null>(
    null,
  );
  const [issue, setIssue] = useState<DictationIssue | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState<number | null>(null);
  /** Seconds until the no-speech auto-stop, during its warning window only. */
  const [silenceStopIn, setSilenceStopIn] = useState<number | null>(null);

  /** Read by the waveform on animation frames — never React state. */
  const levelsRef = useRef<number[]>([]);
  const statusRef = useRef<DictationStatus>("idle");
  const finalTextRef = useRef("");
  /** Mirrors of state that `accept` reads after awaiting Soniox. */
  const tailRef = useRef("");
  const vocabularyRef = useRef<AskHelenaVoiceVocabulary | null>(null);
  const captureRef = useRef<Capture | null>(null);
  const sessionRef = useRef<StreamSession | null>(null);
  /** Audio captured before the socket opens — the first words aren't lost. */
  const queueRef = useRef<ArrayBuffer[]>([]);
  /** For the keepalive `/end` on tab close, which can't await a fresh token. */
  const accessTokenRef = useRef<string | null>(null);
  /** Bumped by discard/accept/unmount, so a start still in flight backs out. */
  const runRef = useRef(0);
  /** When Soniox last returned new words; null until the first. */
  const lastSpeechAtRef = useRef<number | null>(null);
  const liveMicChannelRef = useRef<BroadcastChannel | null>(null);
  /** The latest `stopForIssue`, for callbacks created before it (clock, mic lost). */
  const stopForIssueRef = useRef<(reason: DictationIssue) => Promise<void>>(
    async () => undefined,
  );

  /**
   * A 403 `dictation_disabled` hides the mic for the rest of this visit
   * (handoff). Nothing else gates it: `/capabilities` describes Helena's tools,
   * not dictation, so it is not read (user decision, 2026-10-08).
   */
  const [isDisabledByService, setIsDisabledByService] = useState(false);

  // Mutations and the token getter change identity across renders; the socket
  // handlers are created once per session, so they read the latest via a ref.
  const apiRef = useRef({
    startSession,
    endSession,
    uploadRecording,
    getAccessToken,
  });
  apiRef.current = {
    startSession,
    endSession,
    uploadRecording,
    getAccessToken,
  };

  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const updateStatus = useCallback((next: DictationStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const resetTranscript = useCallback(() => {
    finalTextRef.current = "";
    tailRef.current = "";
    vocabularyRef.current = null;
    setFinalText("");
    setTail("");
    setVocabulary(null);
    setElapsedSeconds(0);
    setMaxSeconds(null);
    setSilenceStopIn(null);
    lastSpeechAtRef.current = null;
    levelsRef.current = [];
    queueRef.current = [];
  }, []);

  /** Exactly once per minted session — on discard, failure and accept alike. */
  const endOnce = useCallback(async (session: StreamSession) => {
    if (session.ended) return;
    session.ended = true;
    try {
      await apiRef.current.endSession({
        sessionId: session.id,
        audioSeconds: elapsedSecondsOf(session),
      });
    } catch (error: unknown) {
      debugConsole(error);
    }
  }, []);

  /**
   * Tears the session down and, on accept, uploads the recording. Runs in the
   * background: neither ✓ nor ✗ waits for it, and an upload failure never
   * blocks sending the text (409 already uploaded / 503 storage off included).
   */
  const finish = useCallback(
    async (upload: boolean) => {
      const session = sessionRef.current;
      const capture = captureRef.current;
      sessionRef.current = null;
      captureRef.current = null;

      if (session) closeSocket(session);
      const recording = capture ? await stopCapture(capture) : null;
      if (!session) return;

      await endOnce(session);
      if (!(upload && recording)) return;
      try {
        await apiRef.current.uploadRecording({
          sessionId: session.id,
          recording,
        });
      } catch (error: unknown) {
        debugConsole(error);
      }
    },
    [endOnce],
  );

  /** The stream ended without ✓/✗: keep the text, release the mic, let ✓/✗ decide. */
  const interrupt = useCallback(
    (reason: DictationIssue) => {
      const session = sessionRef.current;
      const capture = captureRef.current;
      if (statusRef.current !== "listening" || !session) return;

      closeSocket(session);
      if (capture) void stopCapture(capture);
      void endOnce(session);

      tailRef.current = "";
      setTail("");
      setIssue(reason);
      if (finalTextRef.current.trim()) {
        updateStatus("stopped");
      } else {
        void finish(false);
        resetTranscript();
        updateStatus("idle");
      }
    },
    [endOnce, finish, resetTranscript, updateStatus],
  );

  const handleChunk = useCallback(({ pcm, level }: WorkletChunk) => {
    pushAudioLevel(levelsRef.current, level);

    const session = sessionRef.current;
    // After ✓ the end-of-audio frame is sent; nothing may follow it.
    if (session?.closing || session?.finishing) return;
    const ws = session?.ws;
    if (session && ws?.readyState === WebSocket.OPEN) {
      ws.send(pcm);
      session.samplesSent += pcm.byteLength / 2;
    } else {
      queueRef.current.push(pcm);
    }
  }, []);

  const handleTokens = useCallback((tokens: SonioxToken[]) => {
    let committed = "";
    let pending = "";
    for (const token of tokens) {
      if (CONTROL_TOKENS.has(token.text)) continue;
      if (token.is_final) committed += token.text;
      else pending += token.text;
    }
    // New words, final or still changing. A grey tail Soniox merely re-sends
    // unchanged isn't speech, or a stale guess would keep the mic open.
    if (committed.trim() || (pending.trim() && pending !== tailRef.current)) {
      lastSpeechAtRef.current = Date.now();
    }
    if (committed) {
      finalTextRef.current += committed;
      setFinalText(finalTextRef.current);
    }
    // Non-final tokens are re-sent in full on every message, so an empty list
    // means nothing is pending.
    tailRef.current = pending;
    setTail(pending);
  }, []);

  const handleSocketMessage = useCallback(
    (session: StreamSession, data: unknown) => {
      const message = parseSonioxMessage(data);
      if (!message) return;
      if (isSonioxError(message)) {
        debugConsole("Soniox error", message.error_code, message.error_type);
        // Mid-✓: insert what we have rather than fail the accept.
        if (session.finishing) session.onFinished?.();
        else interrupt(socketErrorIssue(session, message));
        return;
      }
      if (message.tokens) handleTokens(message.tokens);
      if (!message.finished) return;
      // Either our end-of-audio frame was honoured (✓), or Soniox ended the
      // stream at the session's time limit.
      if (session.finishing) session.onFinished?.();
      else interrupt({ kind: "time_limit", maxSeconds: session.maxSeconds });
    },
    [handleTokens, interrupt],
  );

  const handleSocketClose = useCallback(
    (session: StreamSession) => {
      if (session.closing) return;
      if (session.finishing) {
        session.onFinished?.();
        return;
      }
      if (!session.opened) {
        // Never connected — a corporate network blocking Soniox, typically.
        setIssue({ kind: "unreachable" });
        void finish(false);
        resetTranscript();
        updateStatus("idle");
        return;
      }
      const ranOut = elapsedSecondsOf(session) >= session.maxSeconds - 1;
      interrupt(
        ranOut
          ? { kind: "time_limit", maxSeconds: session.maxSeconds }
          : { kind: "interrupted" },
      );
    },
    [finish, interrupt, resetTranscript, updateStatus],
  );

  const openStream = useCallback(
    (session: StreamSession, minted: AskHelenaVoiceSession) => {
      const ws = new WebSocket(minted.wsUrl);
      ws.binaryType = "arraybuffer";
      session.ws = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            api_key: minted.apiKey,
            model: minted.model,
            audio_format: "pcm_s16le",
            sample_rate: session.sampleRate,
            num_channels: 1,
            language_hints: minted.languageHints,
            context: {
              general: [{ key: "domain", value: DOMAIN_CONTEXT }],
              terms: minted.vocabulary.terms,
            },
          }),
        );
        for (const pcm of queueRef.current) {
          ws.send(pcm);
          session.samplesSent += pcm.byteLength / 2;
        }
        queueRef.current = [];
        session.opened = true;
        session.openedAt = Date.now();
        updateStatus("listening");
      };
      ws.onmessage = ({ data }: MessageEvent) =>
        handleSocketMessage(session, data);
      ws.onclose = () => handleSocketClose(session);
    },
    [handleSocketClose, handleSocketMessage, updateStatus],
  );

  const refreshAccessToken = useCallback(async () => {
    try {
      accessTokenRef.current =
        (await apiRef.current.getAccessToken(
          import.meta.env.VITE_APP_BASE_URL,
        )) ?? null;
    } catch {
      accessTokenRef.current = null;
    }
  }, []);

  const handleTrackEnded = useCallback(() => {
    void stopForIssueRef.current({ kind: "interrupted" });
  }, []);

  const start = useCallback(async () => {
    if (!isEnabled || statusRef.current !== "idle") return;
    const run = ++runRef.current;
    // Any other composer (this tab or another) that is listening stops.
    liveMicChannelRef.current?.postMessage("started");

    // Before any await: iOS Safari only runs a context created in the tap.
    const ctx = new AudioContext();
    void resumeQuietly(ctx);

    resetTranscript();
    setIssue(null);
    updateStatus("starting");
    void refreshAccessToken();

    const { captured, minted, failure } = await openMicAndSession(
      () => startCapture(ctx, handleChunk, recordsAudio, handleTrackEnded),
      () => apiRef.current.startSession({ language: DICTATION_LANGUAGE }),
    );
    const session = minted ? toStreamSession(minted, ctx.sampleRate) : null;

    const superseded = run !== runRef.current;
    if (superseded || !captured || !minted || !session) {
      releaseHalfStart(ctx, captured, session && (() => void endOnce(session)));
      if (superseded) return;

      if (failure?.kind === "disabled") setIsDisabledByService(true);
      setIssue(failure);
      resetTranscript();
      updateStatus("idle");
      return;
    }

    captureRef.current = captured;
    sessionRef.current = session;
    vocabularyRef.current = minted.vocabulary;
    setVocabulary(minted.vocabulary);
    setMaxSeconds(session.maxSeconds);
    openStream(session, minted);
  }, [
    isEnabled,
    endOnce,
    handleChunk,
    handleTrackEnded,
    openStream,
    refreshAccessToken,
    resetTranscript,
    updateStatus,
    recordsAudio,
  ]);

  const segments: DictationSegment[] = useMemo(
    () => normalizeDictation(finalText, vocabulary),
    [finalText, vocabulary],
  );

  /**
   * ✓ as soon as there is any text, grey or black. Grey words are finalised
   * on press (see `accept`), so the user never waits for them to turn black.
   */
  const canAccept =
    (status === "listening" || status === "stopped") &&
    `${finalText}${tail}`.trim() !== "";

  /**
   * ✓ — ends the audio, lets Soniox finalise every pending word, then returns
   * the corrected text and the session to link it to. `null` if ✗ cancelled
   * it while Soniox was finishing. Leaving the screen doesn't cancel it.
   */
  /**
   * `canAccept`, read from refs: the leave path runs in an unmount cleanup,
   * where the last render may predate the newest words.
   */
  const hasAcceptableText = useCallback(
    () =>
      (statusRef.current === "listening" || statusRef.current === "stopped") &&
      `${finalTextRef.current}${tailRef.current}`.trim() !== "",
    [],
  );

  const accept = useCallback(async (): Promise<AcceptedDictation | null> => {
    const session = sessionRef.current;
    if (!(session && hasAcceptableText())) return null;
    const run = runRef.current;

    if (statusRef.current === "listening") {
      updateStatus("finishing");
      await finalizeStream(session, captureRef.current);
      if (run !== runRef.current) return null;
    }

    // After `finished` the tail is empty. On a timeout it still holds the
    // last guesses, which are kept rather than dropped.
    const text = segmentsToText(
      normalizeDictation(
        `${finalTextRef.current}${tailRef.current}`,
        vocabularyRef.current,
      ),
    ).trim();
    runRef.current += 1;
    // Uploaded whenever there's a recording (`uploadRecording`): with no
    // storage configured the service answers 503 `audio_storage_unconfigured`,
    // which the handoff says to ignore.
    void finish(true);
    resetTranscript();
    setIssue(null);
    updateStatus("idle");
    return text ? { text, voiceSessionId: session.id } : null;
  }, [finish, hasAcceptableText, resetTranscript, updateStatus]);

  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  /** ✗ — drops the text. `/end` still runs; nothing is uploaded. */
  const discard = useCallback(() => {
    runRef.current += 1;
    void finish(false);
    resetTranscript();
    setIssue(null);
    updateStatus("idle");
  }, [finish, resetTranscript, updateStatus]);

  const dismissIssue = useCallback(() => setIssue(null), []);

  /**
   * Ends a dictation from our side — no speech, the time limit, the mic lost,
   * another composer took the mic. Stops the audio and lets Soniox finalise
   * the pending words, then keeps any text for ✓/✗; with nothing said, it
   * closes and leaves the reason for the composer's toast.
   */
  const stopForIssue = useCallback(
    async (reason: DictationIssue) => {
      const session = sessionRef.current;
      if (statusRef.current !== "listening" || !session) return;
      const run = runRef.current;
      updateStatus("finishing");
      setSilenceStopIn(null);
      await finalizeStream(session, captureRef.current);
      // ✗ (or ✓) while Soniox was finalising — that already settled it.
      if (run !== runRef.current) return;
      closeSocket(session);
      void endOnce(session);
      setIssue(reason);
      if (`${finalTextRef.current}${tailRef.current}`.trim()) {
        updateStatus("stopped");
        return;
      }
      void finish(false);
      resetTranscript();
      updateStatus("idle");
    },
    [endOnce, finish, resetTranscript, updateStatus],
  );
  stopForIssueRef.current = stopForIssue;

  // One-second clock: the elapsed timer, the last-30-seconds countdown, and
  // the auto-stops — no new words for NO_SPEECH_TIMEOUT_MS, or the session's
  // time limit. Our own cutoff runs a second before Soniox's, so it still
  // gets to finalise the pending words, and holds even if the key was minted
  // without a duration cap.
  useEffect(() => {
    if (status !== "listening") return;
    const intervalId = setInterval(() => {
      const session = sessionRef.current;
      if (!session?.openedAt) return;
      const now = Date.now();
      const elapsedMs = now - session.openedAt;
      setElapsedSeconds(Math.floor(elapsedMs / 1000));
      if (elapsedMs >= (session.maxSeconds - 1) * 1000) {
        void stopForIssueRef.current({
          kind: "time_limit",
          maxSeconds: session.maxSeconds,
        });
        return;
      }
      const silentMs = now - (lastSpeechAtRef.current ?? session.openedAt);
      if (silentMs >= NO_SPEECH_TIMEOUT_MS) {
        void stopForIssueRef.current({ kind: "no_speech" });
        return;
      }
      const leftMs = NO_SPEECH_TIMEOUT_MS - silentMs;
      setSilenceStopIn(
        leftMs <= NO_SPEECH_WARNING_MS ? Math.ceil(leftMs / 1000) : null,
      );
    }, 1000);
    return () => {
      clearInterval(intervalId);
      setSilenceStopIn(null);
    };
  }, [status]);

  // One live mic: when any other composer — in this tab or another — opens
  // the mic, this one stops (keeping its words), so a forgotten dictation in
  // a background tab can't stay live. BroadcastChannel reaches every other
  // channel object of the same name, same tab included, but not the sender.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(LIVE_MIC_CHANNEL);
    channel.onmessage = () => {
      if (statusRef.current === "listening") {
        void stopForIssue({ kind: "interrupted" });
      } else if (statusRef.current === "starting") {
        discard();
      }
    };
    liveMicChannelRef.current = channel;
    return () => {
      channel.close();
      liveMicChannelRef.current = null;
    };
  }, [discard, stopForIssue]);

  // Tab closing mid-dictation: axios can't outlive the page, keepalive fetch can.
  useEffect(() => {
    const handlePageHide = () => {
      const session = sessionRef.current;
      const accessToken = accessTokenRef.current;
      if (!session || session.ended || !accessToken) return;
      session.ended = true;
      endSessionOnUnload({
        sessionId: session.id,
        audioSeconds: elapsedSecondsOf(session),
        accessToken,
      });
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [endSessionOnUnload]);

  // Leaving the composer (search opened, navigation) releases the mic. Words
  // already dictated are kept: accepted as if ✓ was pressed, then handed to
  // `onLeave`. A ✓ already in flight delivers them itself.
  // Deps must stay stable: if one changed identity, this cleanup would run
  // mid-dictation and accept it.
  useEffect(
    () => () => {
      if (statusRef.current === "finishing") return;
      const onLeaveCallback = onLeaveRef.current;
      if (onLeaveCallback && hasAcceptableText()) {
        acceptRef
          .current()
          .then((accepted) => {
            if (accepted) onLeaveCallback(accepted);
          })
          .catch(reportLeaveFailure);
        return;
      }
      runRef.current += 1;
      void finish(false);
    },
    [finish, hasAcceptableText],
  );

  return {
    /**
     * Mic shown at all: the voice-input switch is on, the browser supports it,
     * and the service hasn't answered 403.
     */
    isAvailable: isEnabled && isDictationSupported() && !isDisabledByService,
    status,
    segments,
    tail,
    canAccept,
    issue,
    elapsedSeconds,
    maxSeconds,
    silenceStopIn,
    levelsRef,
    start,
    accept,
    discard,
    dismissIssue,
  };
};
